import DraftNavigationGuard from "../features/exchange/DraftNavigationGuard";
/* Pipeline 172X: route definitions using React Router v7.
   Routes use real browser URLs; refresh and back/forward are supported.
   Auth guard: unauthenticated → / (landing).
   Onboarding guard: incomplete onboarding → /onboarding. */

import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import { SessionProvider, useSession } from "./SessionContext";

// Page and feature imports
import LandingPage from "../LandingPage";
import SignupPage from "../pages/SignupPage";
import SigninPage from "../pages/SigninPage";
import HostedAlphaAccessPage from "../pages/HostedAlphaAccessPage";
import TrialActivatedPage from "../pages/TrialActivatedPage";
import ProductShell from "../components/ProductShell";

const OnboardingFlow = lazy(
  () => import("../features/onboarding/OnboardingFlow"),
);
const AuthCallbackPage = lazy(() => import("../pages/AuthCallbackPage"));
const VerifyEmailPage = lazy(() => import("../pages/VerifyEmailPage"));
const TodayView = lazy(() => import("../features/TodayView"));
const JobInboxView = lazy(() => import("../features/JobInboxView"));
const JobDetailView = lazy(() => import("../features/JobDetailView"));
const EvidenceStateQAPage = lazy(
  () => import("../features/qa/EvidenceStateQAPage"),
);
const CampaignListPage = lazy(
  () => import("../features/campaigns/CampaignListPage"),
);
const CampaignFormPage = lazy(
  () => import("../features/campaigns/CampaignFormPage"),
);
const CampaignDetailPage = lazy(
  () => import("../features/campaigns/CampaignDetailPage"),
);
const ApplicationsView = lazy(() => import("../features/ApplicationsView"));
const AttentionView = lazy(() => import("../features/AttentionView"));
const AnalyticsView = lazy(() => import("../features/AnalyticsView"));
const AnswersView = lazy(() => import("../features/AnswersView"));
const ExchangeWorkspace = lazy(() => import("../features/ExchangeWorkspace"));
const ProfileView = lazy(() => import("../features/ProfileView"));
const AutomationView = lazy(() => import("../features/AutomationView"));
const SubscriptionView = lazy(() => import("../features/SubscriptionView"));
const PrivacyView = lazy(() => import("../features/PrivacyView"));
const SecuritySessionsView = lazy(
  () => import("../features/SecuritySessionsView"),
);
const PackageStudioView = lazy(() => import("../features/PackageStudioView"));
const PreSubmitView = lazy(() => import("../features/PreSubmitView"));
const AttemptTimelineView = lazy(
  () => import("../features/AttemptTimelineView"),
);

// ─── Navigation helper (bridge between goTo() callbacks and useNavigate) ──────
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { browserAuthMode } from "../lib/supabaseClient";
import { ALPHA_ACCESS_ENABLED } from "../config";

export function useNav() {
  const navigate = useNavigate();
  const location = useLocation();

  return {
    goTo(path: string) {
      navigate(path);
    },
    goBack() {
      navigate(-1);
    },
    currentPath: location.pathname,
  };
}

// ─── Guards ───────────────────────────────────────────────────────────────────
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status, refresh } = useSession();
  if (status === "loading") return <RouteLoading />;
  if (status === "error") return <SessionError onRetry={refresh} />;
  if (status === "anonymous") return <Navigate to="/signin" replace />;
  return <>{children}</>;
}

function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const { status, user, refresh } = useSession();
  if (status === "loading") return <RouteLoading />;
  if (status === "error") return <SessionError onRetry={refresh} />;
  if (status === "anonymous" || !user) {
    return <Navigate to="/signin" replace />;
  }
  if (!user.onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

function RouteLoading() {
  return (
    <main
      aria-busy="true"
      aria-live="polite"
      style={{ minHeight: "100dvh", display: "grid", placeItems: "center" }}
    >
      Loading 172X | Pipeline…
    </main>
  );
}

function SessionError({ onRetry }: { onRetry: () => Promise<void> }) {
  return (
    <main
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 440, textAlign: "center" }}>
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>
          We couldn’t verify your session
        </h1>
        <p style={{ color: "#4b5563", marginBottom: 20 }}>
          172X | Pipeline may be temporarily unavailable. Your work has not been
          submitted or changed.
        </p>
        <button
          type="button"
          onClick={() => void onRetry()}
          style={{
            border: 0,
            borderRadius: 10,
            background: "#1a3a5c",
            color: "white",
            padding: "10px 16px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    </main>
  );
}

function AlphaAccessUnavailable({
  onSignIn,
  onBack,
}: {
  onSignIn: () => void;
  onBack: () => void;
}) {
  return (
    <main
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 480, textAlign: "center" }}>
        <h1 style={{ fontSize: 24, marginBottom: 12 }}>
          Alpha access requests are paused
        </h1>
        <p style={{ color: "#4b5563", lineHeight: 1.6, marginBottom: 20 }}>
          Pipeline is in Private Alpha · Refactoring. New registration is
          paused. Existing Pipeline users can continue to sign in.
        </p>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            justifyContent: "center",
          }}
        >
          <button type="button" onClick={onSignIn}>
            Sign in
          </button>
          <button type="button" onClick={onBack}>
            Back
          </button>
        </div>
      </div>
    </main>
  );
}

// ─── App shell wrapper ────────────────────────────────────────────────────────
function AppShell() {
  const { user } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const currentView = location.pathname.replace("/app/", "") || "today";

  function handleViewChange(view: string) {
    navigate(`/app/${view}`);
  }

  return (
    <ProductShell
      activeView={
        currentView as Parameters<typeof ProductShell>[0]["activeView"]
      }
      onViewChange={handleViewChange}
    >
      <DraftNavigationGuard />
      <Outlet key={user?.id} />
    </ProductShell>
  );
}

function TodayRoute() {
  const navigate = useNavigate();
  return (
    <TodayView
      onViewChange={(view) => navigate(`/app/${view}`)}
      onJobSelect={(campaignId, matchId) =>
        navigate(
          `/app/jobs/${encodeURIComponent(campaignId)}/${encodeURIComponent(matchId)}`,
        )
      }
    />
  );
}

function JobInboxRoute() {
  const navigate = useNavigate();
  return (
    <JobInboxView
      onJobSelect={(campaignId, matchId) =>
        navigate(
          `/app/jobs/${encodeURIComponent(campaignId)}/${encodeURIComponent(matchId)}`,
        )
      }
    />
  );
}

function JobDetailRoute() {
  const navigate = useNavigate();
  const { campaignId = "", matchId = "" } = useParams();
  return (
    <JobDetailView
      campaignId={campaignId}
      matchId={matchId}
      onBack={() => navigate(-1)}
      onPackagePrepared={(packageId) =>
        navigate(`/app/applications/${encodeURIComponent(packageId)}/package`)
      }
    />
  );
}

// ─── Onboarding step route ────────────────────────────────────────────────────
function OnboardingRoute() {
  const navigate = useNavigate();
  return (
    <OnboardingFlow
      onComplete={() => navigate("/app/today", { replace: true })}
    />
  );
}

// ─── Sign-up bridge (keeps NavCallbacks interface) ────────────────────────────
export function SignupRoute() {
  const navigate = useNavigate();
  if (!ALPHA_ACCESS_ENABLED) {
    return (
      <AlphaAccessUnavailable
        onSignIn={() => navigate("/signin", { replace: true })}
        onBack={() => navigate("/", { replace: true })}
      />
    );
  }
  if (browserAuthMode() === "supabase") {
    return <Navigate to="/alpha" replace />;
  }
  return (
    <SignupPage
      goTo={(r) => {
        const map: Record<string, string> = {
          "trial-activated": "/trial-activated",
          signin: "/signin",
          app: "/app/today",
          landing: "/",
        };
        navigate(map[r] ?? "/");
      }}
      goBack={() => navigate(-1)}
    />
  );
}

export function AlphaRoute() {
  const navigate = useNavigate();
  if (!ALPHA_ACCESS_ENABLED) {
    return (
      <AlphaAccessUnavailable
        onSignIn={() => navigate("/signin", { replace: true })}
        onBack={() => navigate("/", { replace: true })}
      />
    );
  }
  if (browserAuthMode() !== "supabase") {
    return <Navigate to="/signup" replace />;
  }
  return (
    <HostedAlphaAccessPage
      onAdmitted={() => navigate("/onboarding", { replace: true })}
      onSignIn={() => navigate("/signin")}
      onBack={() => navigate(-1)}
    />
  );
}

function SigninRoute() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  return (
    <SigninPage
      sessionExpired={false}
      onSignedIn={async (user) => {
        await refresh();
        navigate(user.onboardingComplete ? "/app/today" : "/onboarding", {
          replace: true,
        });
      }}
      goTo={(r) => {
        const map: Record<string, string> = {
          "trial-activated": "/trial-activated",
          signup: "/signup",
          onboarding: "/onboarding",
          app: "/app/today",
          landing: "/",
        };
        navigate(map[r] ?? "/");
      }}
      goBack={() => navigate(-1)}
    />
  );
}

function TrialActivatedRoute() {
  const navigate = useNavigate();
  return (
    <TrialActivatedPage
      goTo={(r) => {
        const map: Record<string, string> = {
          onboarding: "/onboarding",
          app: "/app/today",
          landing: "/",
        };
        navigate(map[r] ?? "/");
      }}
      goBack={() => navigate(-1)}
    />
  );
}

function LandingRoute() {
  const navigate = useNavigate();
  return (
    <LandingPage
      onRequestAlpha={() => navigate("/alpha")}
      onSignIn={() => navigate("/signin")}
    />
  );
}

// ─── Router ───────────────────────────────────────────────────────────────────
export const router = createBrowserRouter([
  { path: "/", element: <LandingRoute /> },
  { path: "/signup", element: <SignupRoute /> },
  { path: "/alpha", element: <AlphaRoute /> },
  { path: "/signin", element: <SigninRoute /> },
  {
    path: "/local-qa/evidence-states",
    element: import.meta.env.DEV ? (
      <EvidenceStateQAPage />
    ) : (
      <Navigate to="/" replace />
    ),
  },
  { path: "/auth/callback", element: <AuthCallbackPage /> },
  { path: "/verify-email", element: <VerifyEmailPage /> },
  {
    path: "/trial-activated",
    element: (
      <RequireAuth>
        <TrialActivatedRoute />
      </RequireAuth>
    ),
  },
  {
    path: "/onboarding",
    element: (
      <RequireAuth>
        <OnboardingRoute />
      </RequireAuth>
    ),
  },
  {
    path: "/onboarding/:step",
    element: (
      <RequireAuth>
        <OnboardingRoute />
      </RequireAuth>
    ),
  },
  {
    path: "/app",
    element: (
      <RequireOnboarding>
        <AppShell />
      </RequireOnboarding>
    ),
    children: [
      { index: true, element: <Navigate to="/app/today" replace /> },
      { path: "today", element: <TodayRoute /> },
      { path: "jobs", element: <JobInboxRoute /> },
      {
        path: "jobs/:campaignId/:matchId",
        element: <JobDetailRoute />,
      },
      { path: "jobs/:matchId", element: <Navigate to="/app/jobs" replace /> },
      { path: "campaigns", element: <CampaignListPage /> },
      { path: "campaigns/new", element: <CampaignFormPage /> },
      {
        path: "campaigns/:campaignId",
        element: <CampaignDetailPage />,
      },
      { path: "applications", element: <ApplicationsView /> },
      {
        path: "applications/:applicationId/package",
        element: <PackageStudioView />,
      },
      {
        path: "applications/:applicationId/pre-submit",
        element: <PreSubmitView />,
      },
      { path: "attention", element: <AttentionView /> },
      { path: "analytics", element: <AnalyticsView /> },
      { path: "answers", element: <AnswersView /> },
      { path: "exchange", element: <ExchangeWorkspace /> },
      { path: "profile", element: <ProfileView /> },
      { path: "automation", element: <AutomationView /> },
      { path: "subscription", element: <SubscriptionView /> },
      { path: "privacy", element: <PrivacyView /> },
      { path: "security/sessions", element: <SecuritySessionsView /> },
      { path: "package-studio", element: <PackageStudioView /> },
      { path: "pre-submit", element: <PreSubmitView /> },
      { path: "attempts/:attemptId", element: <AttemptTimelineView /> },
      { path: "*", element: <Navigate to="/app/today" replace /> },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export default function AppRouter() {
  return (
    <SessionProvider>
      <Suspense fallback={<RouteLoading />}>
        <RouterProvider router={router} />
      </Suspense>
    </SessionProvider>
  );
}
